<script setup lang="ts">
import { ref, computed } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useUiStore } from '@/stores/uiStore'
import { useModal } from '@/composables/useModalStack'
import { useI18n } from '@/i18n'
import { MAX_DOCUMENT_DIMENSION, type DocumentBackground } from '@/types/document'
import * as actions from '@/app/actions'

const emit = defineEmits<{ close: [] }>()
useModal('new-document')
const { t } = useI18n()
const docStore = useDocumentStore()
const ui = useUiStore()

const presets = [
  { name: '1920 × 1080 (FHD)', w: 1920, h: 1080 },
  { name: '1080 × 1080 (SNS)', w: 1080, h: 1080 },
  { name: '2480 × 3508 (A4 300dpi)', w: 2480, h: 3508, dpi: 300 },
  { name: '3840 × 2160 (4K)', w: 3840, h: 2160 },
  { name: '800 × 600', w: 800, h: 600 }
]
const name = ref(t('app.untitled'))
const width = ref(1920)
const height = ref(1080)
const dpi = ref(72)
const bg = ref<'transparent' | 'white' | 'black'>('white')
const valid = computed(() => Number.isInteger(width.value) && Number.isInteger(height.value) && width.value >= 1 && height.value >= 1 && width.value <= MAX_DOCUMENT_DIMENSION && height.value <= MAX_DOCUMENT_DIMENSION)
const memoryMb = computed(() => ((width.value * height.value * 4) / 1048576).toFixed(1))

function applyPreset(e: Event) {
  const p = presets[Number((e.target as HTMLSelectElement).value)]
  if (!p) return
  width.value = p.w
  height.value = p.h
  dpi.value = p.dpi ?? 72
}
function create() {
  if (!valid.value) return
  if (docStore.isDirty && !confirm(t('dialog.close.confirm'))) return
  docStore.newDocument({ name: name.value.trim() || t('app.untitled'), width: width.value, height: height.value, dpi: dpi.value, background: bg.value as DocumentBackground })
  ui.fitToViewport(width.value, height.value)
  emit('close')
  void actions
}
</script>

<template>
  <div class="modal-backdrop" @pointerdown.self="emit('close')">
    <div class="modal" @keydown.esc="emit('close')" @keydown.enter="create">
      <h2>{{ t('dialog.new.title') }}</h2>
      <label class="field"><span>{{ t('dialog.new.name') }}</span><input v-model="name" autofocus /></label>
      <label class="field"><span>{{ t('dialog.new.preset') }}</span>
        <select @change="applyPreset"><option value="">—</option><option v-for="(p, i) in presets" :key="p.name" :value="i">{{ p.name }}</option></select>
      </label>
      <label class="field"><span>{{ t('dialog.new.width') }}</span><span class="row"><input v-model.number="width" type="number" min="1" :max="MAX_DOCUMENT_DIMENSION" /> px</span></label>
      <label class="field"><span>{{ t('dialog.new.height') }}</span><span class="row"><input v-model.number="height" type="number" min="1" :max="MAX_DOCUMENT_DIMENSION" /> px</span></label>
      <label class="field"><span>{{ t('dialog.new.dpi') }}</span><input v-model.number="dpi" type="number" min="1" max="10000" /></label>
      <label class="field"><span>{{ t('dialog.new.background') }}</span>
        <select v-model="bg">
          <option value="white">{{ t('dialog.new.bg.white') }}</option>
          <option value="black">{{ t('dialog.new.bg.black') }}</option>
          <option value="transparent">{{ t('dialog.new.bg.transparent') }}</option>
        </select>
      </label>
      <p class="muted mono">{{ width }} × {{ height }} px ≈ {{ memoryMb }} MB / layer</p>
      <div class="actions">
        <button @click="emit('close')">{{ t('dialog.cancel') }}</button>
        <button class="primary" :disabled="!valid" @click="create">{{ t('dialog.new.create') }}</button>
      </div>
    </div>
  </div>
</template>
